import { QuestionGroupType } from '@exam-management/graphql';
import { ConfirmationModal, DataGrid, mergeSx, type DataGridProps } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useSnackbar } from '@shared/snackbar';
import { useCallback, useMemo, useState } from 'react';

import { createColumns, type TaskManagementQuestionListType } from './columns';
import QuestionListNoRowOverlay from './no-row-overlay';

export interface TaskManagementQuestionListDataGridProps
  extends Omit<DataGridProps, 'columns' | 'rows' | 'onRowClick'> {
  rows: TaskManagementQuestionListType[];
  onRemoveQuestion: (row: TaskManagementQuestionListType) => void;
  onRemoveItemSet: (row: TaskManagementQuestionListType) => void;
  onRowClick?: (row: TaskManagementQuestionListType) => void;
}

export default function TaskManagementQuestionListDataGrid(
  props: Readonly<TaskManagementQuestionListDataGridProps>,
) {
  const { sx, rows, onRemoveQuestion, onRemoveItemSet, onRowClick, ...restProps } = props;
  const { showSnackbar } = useSnackbar();
  const [selectedItemSetRow, setSelectedItemSetRow] =
    useState<TaskManagementQuestionListType | null>(null);
  const [isConfirmModalOpen, confirmModalHandler] = useDisclosure(false);

  const handleRemove = useCallback(
    (row: TaskManagementQuestionListType) => {
      if (row.questionGroupType === QuestionGroupType.ItemSet) {
        setSelectedItemSetRow(row);
        confirmModalHandler.open();
        return;
      }
      onRemoveQuestion(row);
      showSnackbar({ message: 'Question removed successfully', variant: 'success' });
    },
    [confirmModalHandler, onRemoveQuestion, showSnackbar],
  );

  const handleConfirmRemoveItemSet = () => {
    if (!selectedItemSetRow) return;

    confirmModalHandler.close();
    onRemoveItemSet?.(selectedItemSetRow);
    showSnackbar({ message: 'Questions removed successfully', variant: 'success' });
    setSelectedItemSetRow(null);
  };

  const handleCloseConfirmModal = () => {
    confirmModalHandler.close();
    setSelectedItemSetRow(null);
  };

  const columns = useMemo(
    () =>
      createColumns({
        onRemove: handleRemove,
      }),
    [handleRemove],
  );

  return (
    <>
      <DataGrid
        columns={columns}
        rows={rows}
        rowHeight={48}
        columnHeaderHeight={48}
        disableColumnMenu
        disableRowSelectionOnClick
        hideFooter
        slots={{ noRowsOverlay: QuestionListNoRowOverlay }}
        onRowClick={onRowClick ? ({ row }) => onRowClick(row) : undefined}
        {...restProps}
        sx={mergeSx(
          (theme) => ({
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.shape.rounded(2),
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            border: `1px solid ${theme.palette.grey[200]}`,
            boxShadow: 'none',
            '.MuiDataGrid-cell': {
              padding: theme.spacing(3),
            },
            '.MuiDataGrid-columnHeader': {
              padding: theme.spacing(3),
              backgroundColor: theme.palette.grey[100],
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            '.MuiDataGrid-row > .MuiDataGrid-withBorderColor': {
              borderColor: theme.palette.divider,
            },
            '.MuiDataGrid-cell:last-child': {
              padding: theme.spacing(1.5),
            },
            '.MuiDataGrid-cellCheckbox': {
              padding: theme.spacing(0),
            },
            '.MuiDataGrid-overlayWrapper': {
              minHeight: theme.spacing(20),
            },
          }),
          sx,
        )}
      />
      <ConfirmationModal
        title='Remove Item Set?'
        content={
          <>
            Item Set นี้มีทั้งหมด {selectedItemSetRow?.questionIds?.length} ข้อ การลบนี้
            <br />
            จะเป็นการลบทุกข้อออกจากรายการพร้อมกัน
          </>
        }
        acceptText='Confirm'
        open={isConfirmModalOpen}
        onAccept={handleConfirmRemoveItemSet}
        onDiscard={handleCloseConfirmModal}
        onClose={handleCloseConfirmModal}
      />
    </>
  );
}
